import { useRef, useEffect } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Source } from "../app";
import { useLegalRefs } from "../hooks/useLegalRefs";

interface AnswerProps {
  content: string;
  sources: Source[];
  isStreaming?: boolean;
  onSourceClick?: (url: string) => void;
}

export function Answer({ content, sources, isStreaming, onSourceClick }: AnswerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(0);

  // Process content to linkify legal references (only when streaming is complete)
  const { processedContent } = useLegalRefs(content, !isStreaming, {
    waitForComplete: true,
    enabled: true,
  });

  // Smooth scroll effect - keep track of content growth
  useEffect(() => {
    if (isStreaming && containerRef.current) {
      const currentLength = content.length;
      if (currentLength > prevLengthRef.current) {
        prevLengthRef.current = currentLength;
      }
    } else {
      prevLengthRef.current = 0;
    }
  }, [content, isStreaming]);

  // Custom link renderer to add footnote references for Legifrance links
  const renderLink = ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (!href) return <>{children}</>;

    const isLegifranceLink = href.includes("legifrance.gouv.fr");
    const isKaliLink = href?.includes("/conv_coll/id/");
    const isLegiLink = href?.includes("/codes/article_lc/") || href?.includes("/codes/section_lc/");

    if (isLegifranceLink && isKaliLink) {
      // KALI convention article link
      const articleIdMatch = href.match(/\/id\/([^/]+)/);
      const articleId = articleIdMatch?.[1];

      // Find matching source
      const sourceIndex = sources.findIndex((s) => articleId && s.articleUrl.includes(articleId));

      // if (sourceIndex !== -1) {
      const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (onSourceClick && href) {
          onSourceClick(href);
        }
      };

      return (
        <>
          <a href={href} onClick={handleClick} className="article-ref" title="Cliquez pour apercu">
            {children}
          </a>
          {sourceIndex !== -1 && (
            <sup className="footnote-ref">
              <a href={href} onClick={handleClick} title="Apercu dans le panneau">
                [{sourceIndex + 1}]
              </a>
            </sup>
          )}
        </>
      );
      // }
    }

    if (isLegifranceLink && isLegiLink) {
      // LEGI Code du travail article link (auto-detected references)
      const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (onSourceClick && href) {
          onSourceClick(href);
        }
      };

      return (
        <a
          href={href}
          onClick={handleClick}
          className="article-ref legi-ref"
          title="Voir sur Legifrance"
        >
          {children}
        </a>
      );
    }

    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  };

  return (
    <div ref={containerRef} className={`answer-content ${isStreaming ? "is-streaming" : ""}`}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: renderLink,
          // Add custom table styling
          table: ({ children }) => (
            <div className="table-wrapper">
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {processedContent}
      </Markdown>
    </div>
  );
}
